import React from "react";
import { Link } from "react-router-dom";
import { Mail } from "lucide-react";

const SUPPORT_EMAIL = "blake@sweeplaunch.com";
const LAST_UPDATED = "June 20, 2026";

const Section = ({ id, title, children }) => (
  <section id={id} className="scroll-mt-24" data-testid={`tos-section-${id}`}>
    <h2 className="font-display text-2xl font-bold tracking-tight text-zinc-950 mt-12 mb-4">{title}</h2>
    <div className="space-y-4 text-[15px] leading-relaxed text-zinc-700">{children}</div>
  </section>
);

const TOC = [
  ["acceptance", "Acceptance of Terms"],
  ["service", "Description of the Service"],
  ["accounts", "Accounts & Eligibility"],
  ["billing", "Plans, Billing & Cancellation"],
  ["merchant-responsibilities", "Merchant Responsibilities"],
  ["sweepstakes-law", "Sweepstakes & Promotion Law"],
  ["acceptable-use", "Acceptable Use"],
  ["ip", "Intellectual Property"],
  ["third-party", "Third-Party Services"],
  ["disclaimers", "Disclaimers"],
  ["liability", "Limitation of Liability"],
  ["indemnification", "Indemnification"],
  ["termination", "Termination"],
  ["changes", "Changes to These Terms"],
  ["contact", "Contact Us"],
];

export default function TermsOfService() {
  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-xl border-b border-zinc-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2" data-testid="tos-home-link">
            <img src="/assets/app-icon-1200.png" alt="SweepLaunch" className="size-7 rounded-md object-cover" />
            <span className="font-display font-bold text-lg tracking-tight">SweepLaunch</span>
          </Link>
          <a href={`mailto:${SUPPORT_EMAIL}`} className="text-sm font-medium text-zinc-700 hover:text-zinc-950" data-testid="tos-support-link">
            Contact support
          </a>
        </div>
      </header>

      <div className="max-w-3xl mx-auto px-6 py-16" data-testid="tos-root">
        <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Legal</div>
        <h1 className="font-display text-4xl sm:text-5xl font-bold tracking-tighter text-zinc-950 mt-2">Terms of Service</h1>
        <p className="text-sm text-zinc-500 mt-3">Last updated: {LAST_UPDATED}</p>

        {/* Table of contents */}
        <nav className="mt-8 bg-white border border-zinc-200 rounded-md p-5">
          <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold mb-3">On this page</div>
          <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
            {TOC.map(([id, label]) => (
              <li key={id}>
                <a href={`#${id}`} className="text-zinc-700 hover:text-zinc-950 hover:underline">{label}</a>
              </li>
            ))}
          </ul>
        </nav>

        <Section id="acceptance" title="Acceptance of Terms">
          <p>
            These Terms of Service (“Terms”) govern your access to and use of SweepLaunch (“we”, “us”, “our”), including our
            website, our Shopify application, and all related services (collectively, the “Service”). By installing, accessing,
            or using the Service, you agree to be bound by these Terms. If you are using the Service on behalf of a business,
            you represent that you have authority to bind that business, and “you” refers to that business.
          </p>
          <p>If you do not agree to these Terms, do not install or use the Service.</p>
        </Section>

        <Section id="service" title="Description of the Service">
          <p>
            SweepLaunch is a sweepstakes and giveaway platform for Shopify merchants. It provides tools to track entries,
            convert qualifying purchases into entries based on a ratio you configure, manage Alternative Method of Entry (AMOE)
            submissions, detect fraudulent entries, generate official rules, run a live leaderboard, draw winners using a
            weighted random selection, and estimate giveaway performance via the “Giveaway Success Score.”
          </p>
          <p>
            We may add, modify, or remove features of the Service at any time. The Service is provided on a subscription basis
            as described below.
          </p>
        </Section>

        <Section id="accounts" title="Accounts & Eligibility">
          <p>
            To use the Service you must connect a Shopify store or sign in with a supported authentication method. You are
            responsible for maintaining the confidentiality of your account and for all activity that occurs under it. You must
            be at least 18 years old and capable of forming a binding contract to use the Service.
          </p>
          <p>
            You agree to provide accurate information and to keep it current. We may suspend or terminate accounts that contain
            false information or that are used in violation of these Terms.
          </p>
        </Section>

        <Section id="billing" title="Plans, Billing & Cancellation">
          <p>
            Paid plans are billed exclusively through <strong>Shopify Billing</strong>. Subscription charges appear on your
            regular Shopify invoice. We do not collect, store, or process your payment card or bank details — all payment
            credentials are handled by Shopify.
          </p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Plans are billed on a recurring basis (every 30 days) at the price shown at the time of subscription.</li>
            <li>Plan selection, upgrades, downgrades, and cancellations are managed through Shopify’s managed pricing page.</li>
            <li>Cancellations take effect at the end of the current billing period; charges already incurred are non-refundable except where required by law.</li>
            <li>Uninstalling the app cancels your subscription and revokes our access to your store data.</li>
          </ul>
        </Section>

        <Section id="merchant-responsibilities" title="Merchant Responsibilities">
          <p>
            You are solely responsible for the giveaways and promotions you create using the Service, including their prizes,
            terms, eligibility criteria, and conduct. You act as the <strong>data controller</strong> for participant data and
            are responsible for obtaining all necessary consents and for fulfilling participant privacy requests. SweepLaunch
            acts as a <strong>data processor</strong> on your behalf.
          </p>
        </Section>

        <Section id="sweepstakes-law" title="Sweepstakes & Promotion Law">
          <p>
            Sweepstakes, contests, and giveaways are regulated and the applicable rules vary by jurisdiction. While SweepLaunch
            provides tools such as an official rules generator and an AMOE (no-purchase) entry portal to help you operate
            compliant promotions, <strong>SweepLaunch does not provide legal advice</strong>. You are responsible for ensuring
            that each promotion you run complies with all applicable laws, regulations, and platform policies, including any
            registration, bonding, disclosure, and no-purchase-necessary requirements.
          </p>
        </Section>

        <Section id="acceptable-use" title="Acceptable Use">
          <p>You agree not to use the Service to:</p>
          <ul className="list-disc pl-5 space-y-1.5">
            <li>Run illegal lotteries or promotions that require a purchase where prohibited by law.</li>
            <li>Send unsolicited communications or violate anti-spam laws.</li>
            <li>Manipulate entries, winner selection, or fraud controls, or interfere with the integrity of the Service.</li>
            <li>Upload malicious code, attempt unauthorized access, or scrape or overload the Service.</li>
            <li>Infringe the intellectual property or privacy rights of others.</li>
          </ul>
          <p>We may suspend or terminate access for any violation of this section.</p>
        </Section>

        <Section id="ip" title="Intellectual Property">
          <p>
            The Service, including its software, design, and content (excluding your data and your store content), is owned by
            SweepLaunch and protected by intellectual property laws. We grant you a limited, non-exclusive, non-transferable
            license to use the Service during your subscription. You retain ownership of your store data and the content you
            create; you grant us the rights necessary to process that data to provide the Service.
          </p>
        </Section>

        <Section id="third-party" title="Third-Party Services">
          <p>
            The Service integrates with third-party platforms such as Shopify and, optionally, email and marketing providers.
            Your use of those platforms is governed by their own terms and privacy policies. We are not responsible for the
            availability, content, or practices of third-party services.
          </p>
        </Section>

        <Section id="disclaimers" title="Disclaimers">
          <p>
            The Service is provided “as is” and “as available” without warranties of any kind, whether express or implied,
            including warranties of merchantability, fitness for a particular purpose, and non-infringement. We do not warrant
            that the Service will be uninterrupted, error-free, or that performance estimates (such as the Giveaway Success
            Score) will reflect actual results, which depend on factors outside our control.
          </p>
        </Section>

        <Section id="liability" title="Limitation of Liability">
          <p>
            To the maximum extent permitted by law, SweepLaunch and its affiliates will not be liable for any indirect,
            incidental, special, consequential, or punitive damages, or any loss of profits, revenue, data, or goodwill. Our
            total liability for any claim arising out of or relating to the Service will not exceed the amount you paid to us in
            the twelve (12) months preceding the event giving rise to the claim.
          </p>
        </Section>

        <Section id="indemnification" title="Indemnification">
          <p>
            You agree to indemnify and hold harmless SweepLaunch and its affiliates from any claims, damages, liabilities, and
            expenses (including reasonable legal fees) arising out of your use of the Service, your giveaways and promotions, or
            your violation of these Terms or applicable law.
          </p>
        </Section>

        <Section id="termination" title="Termination">
          <p>
            You may stop using the Service at any time by canceling your subscription and uninstalling the app. We may suspend
            or terminate your access if you violate these Terms or if required to protect the Service or other users. Upon
            termination, your right to use the Service ceases, and we will handle your data in accordance with our Privacy
            Policy and Shopify’s data-redaction requirements.
          </p>
        </Section>

        <Section id="changes" title="Changes to These Terms">
          <p>
            We may update these Terms from time to time. Material changes will be reflected by the “Last updated” date above and,
            where appropriate, communicated in-app or by email. Your continued use of the Service after changes take effect
            constitutes acceptance of the revised Terms.
          </p>
        </Section>

        <Section id="contact" title="Contact Us">
          <p>Questions about these Terms? We’re here to help.</p>
          <div className="bg-white border border-zinc-200 rounded-md p-5 flex items-center gap-3" data-testid="tos-contact-card">
            <div className="size-10 rounded-md bg-zinc-950 flex items-center justify-center">
              <Mail className="size-5 text-white" strokeWidth={1.5} />
            </div>
            <div>
              <div className="text-[11px] uppercase tracking-[0.2em] text-zinc-500 font-semibold">Support & Legal</div>
              <a href={`mailto:${SUPPORT_EMAIL}`} className="font-display font-bold text-zinc-950 hover:underline" data-testid="tos-contact-email">{SUPPORT_EMAIL}</a>
            </div>
          </div>
          <p className="text-sm text-zinc-500">
            See also our <Link to="/privacy" className="text-zinc-950 underline" data-testid="tos-privacy-link">Privacy Policy</Link>.
          </p>
        </Section>

        <div className="mt-16 pt-8 border-t border-zinc-200 flex items-center justify-between text-sm text-zinc-500">
          <span>© {new Date().getFullYear()} SweepLaunch</span>
          <Link to="/" className="hover:text-zinc-950" data-testid="tos-back-home">← Back to home</Link>
        </div>
      </div>
    </div>
  );
}
